// `@hopdrive/sdk-ui/theme` rather than `@hopdrive/ui/theme`, because this one
// already includes the keyboard focus ring in `components`.
import { toMUITheme } from '@hopdrive/sdk-ui/theme';
import { allTokens } from '@hopdrive/ui/tokens';
import { createTheme, css } from '@mui/material/styles';
import deepmerge from 'deepmerge';

// Extend the MUI theme to include our custom properties
declare module '@mui/material/styles' {
  interface PaletteColor {
    darker?: string;
    lighter?: string;
  }

  interface SimplePaletteColorOptions {
    darker?: string;
    lighter?: string;
  }

  interface Palette {
    utility: PaletteColor;
    nav: {
      header: string;
      sidebar: string;
      contrastText: string;
      prod: string;
      test: string;
    };
    chart: {
      gridStroke: string;
      axisTick: string;
      identifier: string;
      series: string[];
    };
    tertiary: PaletteColor;
    dividerContrast?: string;
    ops: PaletteColor;
    concierge: PaletteColor;
    loaner: PaletteColor;
    shuttle: PaletteColor;
    auto: PaletteColor;
    lyft: PaletteColor;
    uber: PaletteColor;
    android: PaletteColor;
    ios: PaletteColor;
  }

  interface PaletteOptions {
    utility?: SimplePaletteColorOptions;
    nav?: {
      header?: string;
      sidebar?: string;
      contrastText?: string;
      prod?: string;
      test?: string;
    };
    chart?: {
      gridStroke?: string;
      axisTick?: string;
      identifier?: string;
      series?: string[];
    };
    tertiary?: SimplePaletteColorOptions;
    dividerContrast?: string;
    ops?: SimplePaletteColorOptions;
    concierge?: SimplePaletteColorOptions;
    loaner?: SimplePaletteColorOptions;
    shuttle?: SimplePaletteColorOptions;
    auto?: SimplePaletteColorOptions;
    lyft?: SimplePaletteColorOptions;
    uber?: SimplePaletteColorOptions;
    android?: SimplePaletteColorOptions;
    ios?: SimplePaletteColorOptions;
  }

  interface TypeBackground {
    alt?: string;
    dark?: string;
  }

  interface TypeAction {
    contrastActive?: string;
    contrastHover?: string;
    contrastSelected?: string;
    contrastDisabled?: string;
    contrastDisabledBackground?: string;
    contrastFocus?: string;
  }

  interface TypeText {
    contrastPrimary?: string;
    contrastSecondary?: string;
    contrastDisabled?: string;
  }
}

// For Paper variants like 'custom' and 'customPrint'
declare module '@mui/material/Paper' {
  interface PaperPropsVariantOverrides {
    custom: true;
    customPrint: true;
  }
}

// For Typography variants like 'body3', 'PageHeader'
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    body3: true;
    body4: true;
    PageHeader: true;
    SectionHeader: true;
  }
}

// Get the base design system theme
const baseTheme = toMUITheme('light');

// Service-specific colors
const serviceColors = {
  ops: {
    main: '#f05020',
    light: '#f37350',
    dark: '#c04019',
    contrastText: '#fff',
  },
  concierge: {
    main: '#4080c8',
    light: '#6699d3',
    dark: '#3366a0',
    contrastText: '#fff',
  },
  loaner: {
    main: '#2b5585',
    light: '#55779d',
    dark: '#22446a',
    contrastText: '#fff',
  },
  shuttle: {
    main: '#20a090',
    light: '#4db3a6',
    dark: '#198073',
    contrastText: '#fff',
  },
  auto: {
    main: '#20c880',
    light: '#4dd399',
    dark: '#19a066',
    contrastText: '#fff',
  },
  lyft: {
    main: '#ea0b8c',
    light: '#ee3ca3',
    dark: '#bb0970',
    contrastText: '#fff',
  },
  uber: {
    main: '#000000',
    light: '#333333',
    dark: '#000000',
    contrastText: '#fff',
  },
  android: {
    main: '#00de7a',
    light: '#33e595',
    dark: '#00b162',
    contrastText: '#fff',
  },
  ios: {
    main: '#b3b3b3',
    light: '#c2c2c2',
    dark: '#8f8f8f',
    contrastText: '#000',
  },
};

// Custom overrides on top of the design system theme
// NOTE: Using a plain object, NOT createTheme(), to avoid MUI filling in
// default palette values (like primary: #1976d2) that would overwrite
// the design system's primary red during deepmerge.
const customOverrides: Parameters<typeof createTheme>[0] = {
  typography: {
    fontWeightRegular: 500,
    fontWeightMedium: 600,
    h3: { fontWeight: 700 },
    h4: { fontWeight: 700 },
    h5: { fontWeight: 700 },
    h6: { fontWeight: 700 },
    subtitle1: { fontWeight: 600 },
    subtitle2: { fontWeight: 600 },
    body1: { fontWeight: 500 },
    body2: { fontWeight: 500 },
  },
  palette: {
    // Nav palette
    nav: {
      header: '#ffffffe8',
      sidebar: allTokens.palette.grounding.main,
      contrastText: '#fff',
      prod: allTokens.palette.grounding.main,
      test: '#666d80',
    },
    dividerContrast: '#ffffff18',

    // Chart palette — Recharts can't accept MUI tokens, so resolved hexes are exposed here
    chart: {
      gridStroke: '#e1e3e5',
      axisTick: '#788088',
      identifier: '#4068a0',
      series: ['#4068a0', '#40a0b0', '#ff4830', '#f4b020', '#20b020', '#7b61ff', '#e06090', '#ff8a50'],
    },

    // Utility palette (from design system tokens)
    utility: {
      main: '#404860',
      light: '#757b8d',
      dark: '#2a2f3e',
      lighter: '#e2e4e7',
      contrastText: '#fff',
    },

    // Tertiary palette (tertiaryAccent from design system tokens)
    tertiary: {
      main: allTokens.palette.tertiaryAccent.main,
      light: allTokens.palette.tertiaryAccent[200],
      dark: allTokens.palette.tertiaryAccent[700],
      contrastText: '#fff',
    },

    // Service colors
    ...serviceColors,

    // Background
    background: {
      default: '#f4f6f8',
      alt: '#f0f2f4',
      dark: '#e8eaec',
      paper: '#ffffff',
    },

    // Contrast action/text colors
    action: {
      contrastActive: '#ffffff80',
      contrastHover: '#ffffff0c',
      contrastSelected: '#ffffff18',
      contrastDisabled: '#ffffff50',
      contrastDisabledBackground: '#ffffff24',
      contrastFocus: '#ffffff24',
    },
    text: {
      contrastPrimary: '#ffffff',
      contrastSecondary: '#ffffffa0',
      contrastDisabled: '#ffffff72',
    },
  },

  // Component overrides. Do not add a focus ring here, `toMUITheme` already
  // returns one for the `ButtonBase` family.
  components: {
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      variants: [
        {
          props: { variant: 'custom' },
          style: css`
            border-radius: ${allTokens.radius.lg}px;
            box-shadow: ${allTokens.elevation.level2.layers.map(l => `${l.offsetX}px ${l.offsetY}px ${l.blur}px ${l.spread}px rgba(0,0,0,${l.opacity})`).join(', ')};
          `,
        },
        {
          props: { variant: 'customPrint' },
          style: css`
            border-radius: ${allTokens.radius.lg}px;
            border: 1px solid #20243050;
          `,
        },
      ],
    },
    MuiTypography: {
      variants: [
        {
          props: { variant: 'body3' },
          style: css`
            line-height: 1.333;
            font-family: 'Inter Tight', 'Roboto', 'Helvetica', 'Arial', sans-serif;
            font-size: 12px;
            font-weight: 400;
          `,
        },
        {
          props: { variant: 'body4' },
          style: css`
            line-height: 1.333;
            font-family: 'Inter Tight', 'Roboto', 'Helvetica', 'Arial', sans-serif;
            font-size: 10px;
            font-weight: 400;
          `,
        },
        {
          props: { variant: 'PageHeader' },
          style: css`
            display: 'inline';
            vertical-align: 'top';
            line-height: 1rem;
            font-size: 24px;
            font-weight: 600;
          `,
        },
        {
          props: { variant: 'SectionHeader' },
          style: css`
            line-height: 1;
            font-size: 21px;
            font-weight: 600;
            cursor: default;
          `,
        },
      ],
    },
  },
};

// Deep merge the base design system theme with our custom overrides
const theme = createTheme(deepmerge(baseTheme, customOverrides));

export default theme;
